<script lang="ts">
  // Prozedurale Bilder für Bildfragen (Flaggen u. a.) – als Canvas gezeichnet
  import { onMount } from 'svelte';
  import type { ImageSpec } from '../../../shared/types.ts';
  import { t } from '../../i18n.ts';

  let { spec }: { spec: ImageSpec } = $props();
  let cv: HTMLCanvasElement;

  onMount(() => {
    const W = 360, H = spec.kind === 'flag' ? Math.round(360 / (spec.ratio ?? 1.5)) : 240;
    cv.width = W * 2;
    cv.height = H * 2;
    const ctx = cv.getContext('2d')!;
    ctx.scale(2, 2);
    if (spec.kind === 'flag') drawFlag(ctx, spec, W, H);
  });

  function drawFlag(ctx: CanvasRenderingContext2D, f: Extract<ImageSpec, { kind: 'flag' }>, W: number, H: number) {
    const c = f.colors;
    const weights = f.weights ?? c.map(() => 1);
    const sum = weights.reduce((a, b) => a + b, 0);
    switch (f.layout) {
      case 'h': {
        let y = 0;
        c.forEach((col, i) => {
          const h = (weights[i] / sum) * H;
          ctx.fillStyle = col;
          ctx.fillRect(0, y, W, h + 1);
          y += h;
        });
        break;
      }
      case 'v': {
        let x = 0;
        c.forEach((col, i) => {
          const w = (weights[i] / sum) * W;
          ctx.fillStyle = col;
          ctx.fillRect(x, 0, w + 1, H);
          x += w;
        });
        break;
      }
      case 'nordic': {
        // Hintergrund, Kreuz (außen), optional inneres Kreuz
        ctx.fillStyle = c[0];
        ctx.fillRect(0, 0, W, H);
        const cx = W * 0.36, t1 = H * 0.2;
        ctx.fillStyle = c[1];
        ctx.fillRect(cx - t1 / 2, 0, t1, H);
        ctx.fillRect(0, H / 2 - t1 / 2, W, t1);
        if (c[2]) {
          const t2 = t1 * 0.5;
          ctx.fillStyle = c[2];
          ctx.fillRect(cx - t2 / 2, 0, t2, H);
          ctx.fillRect(0, H / 2 - t2 / 2, W, t2);
        }
        break;
      }
      case 'circle':
        ctx.fillStyle = c[0];
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = c[1];
        ctx.beginPath();
        ctx.arc(W * (f.weights?.[0] ?? 0.5), H / 2, H * 0.3, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'cross':
        ctx.fillStyle = c[0];
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = c[1];
        ctx.fillRect(W / 2 - H * 0.1, H * 0.2, H * 0.2, H * 0.6);
        ctx.fillRect(W / 2 - H * 0.3, H / 2 - H * 0.1, H * 0.6, H * 0.2);
        break;
      case 'diag':
        ctx.fillStyle = c[0];
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(W, 0);
        ctx.lineTo(0, H);
        ctx.fill();
        ctx.fillStyle = c[1];
        ctx.beginPath();
        ctx.moveTo(W, 0);
        ctx.lineTo(W, H);
        ctx.lineTo(0, H);
        ctx.fill();
        break;
      case 'quarters':
        [[0, 0], [1, 0], [0, 1], [1, 1]].forEach(([x, y], i) => {
          ctx.fillStyle = c[i % c.length];
          ctx.fillRect((x * W) / 2, (y * H) / 2, W / 2 + 1, H / 2 + 1);
        });
        break;
      case 'saltire':
        ctx.fillStyle = c[0];
        ctx.fillRect(0, 0, W, H);
        ctx.strokeStyle = c[1];
        ctx.lineWidth = H * 0.2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(W, H);
        ctx.moveTo(W, 0);
        ctx.lineTo(0, H);
        ctx.stroke();
        break;
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, W - 2, H - 2);
  }
</script>

<canvas bind:this={cv} class="img" role="img" aria-label={t('quiz.imageAlt')} data-testid="quiz-image"></canvas>

<style>
  .img {
    display: block;
    width: min(320px, 100%);
    margin: 0 auto 14px;
    border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  }
</style>
